import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { getModeFromPath } from "../utils/getMode";
import { modeTheme } from "../theme/modeTheme";

import ChurchLogo from "../../assets/logos/Church-Logo.png";
import ChurchIcon from "../../assets/logos/Oikos Church Icon.png";

const loadedLoadingIcons = new Set();

const modeLabelMap = {
  church: "Church",
};

function isIconReady(src = "") {
  return !src || loadedLoadingIcons.has(src);
}

function loadLoadingIcon(src = "") {
  if (!src || loadedLoadingIcons.has(src) || typeof window === "undefined") {
    return Promise.resolve(src);
  }

  return new Promise((resolve) => {
    const image = new Image();
    image.onload = async () => {
      try {
        if (typeof image.decode === "function") await image.decode();
      } catch (_error) {
        // Loaded is enough for this short transition.
      }
      loadedLoadingIcons.add(src);
      resolve(src);
    };
    image.onerror = () => resolve("");
    image.src = src;
  });
}

function hexToRgb(color = "") {
  const clean = String(color || "").trim().replace("#", "");
  const normalized = clean.length === 3
    ? clean.split("").map((item) => `${item}${item}`).join("")
    : clean;
  if (!/^[0-9a-f]{6}$/i.test(normalized)) return null;
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function colorWithAlpha(color = "", alpha = 1) {
  const rgb = hexToRgb(color);
  if (!rgb) return `rgba(95, 125, 77, ${alpha})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

export default function GlobalLoadingPage({
  title = "Loading",
  detail = "Preparing your workspace...",
  modeOverride = "",
  primaryColorOverride = "",
  iconOverride = "",
  iconFallbackOverride = "",
  labelOverride = "",
  backgroundImageOverride = "",
}) {
  const location = useLocation();
  const mode = modeOverride || getModeFromPath(location.pathname, window.location.hostname);
  const primaryColor = primaryColorOverride || modeTheme[mode]?.primary || "#5F7D4D";
  const modeLabel = labelOverride || modeLabelMap[mode] || "Church";
  const requestedIconImage = iconOverride || ChurchIcon;
  const fallbackIconImage = iconFallbackOverride || ChurchLogo;
  const [loadedIconImage, setLoadedIconImage] = useState(() => (
    isIconReady(requestedIconImage) ? requestedIconImage : fallbackIconImage
  ));
  const [iconImageReady, setIconImageReady] = useState(() => (
    isIconReady(requestedIconImage) || (requestedIconImage && requestedIconImage === fallbackIconImage)
  ));
  const fallbackLetter = String(modeLabel || "C").trim().slice(0, 1).toUpperCase() || "C";

  useEffect(() => {
    let cancelled = false;
    const immediateIcon = isIconReady(requestedIconImage) ? requestedIconImage : fallbackIconImage;
    setIconImageReady(isIconReady(immediateIcon) || (immediateIcon && immediateIcon === requestedIconImage));
    setLoadedIconImage(immediateIcon);
    loadLoadingIcon(requestedIconImage).then((nextIcon) => {
      if (!cancelled && nextIcon) {
        setIconImageReady(isIconReady(nextIcon));
        setLoadedIconImage(nextIcon);
      }
    });
    if (fallbackIconImage && fallbackIconImage !== requestedIconImage) {
      loadLoadingIcon(fallbackIconImage);
    }
    return () => {
      cancelled = true;
    };
  }, [fallbackIconImage, requestedIconImage]);

  return (
    <div
      style={{
        ...styles.page,
        background: `linear-gradient(180deg, ${colorWithAlpha(primaryColor, 0.08)} 0%, ${colorWithAlpha(primaryColor, 0.16)} 100%)`,
      }}
    >
      {backgroundImageOverride ? (
        <img
          aria-hidden="true"
          alt=""
          src={backgroundImageOverride}
          style={styles.backgroundImage}
        />
      ) : null}
      <div
        style={{
          ...styles.backdrop,
          background: `radial-gradient(circle at 50% 34%, ${colorWithAlpha(primaryColor, 0.18)}, transparent 34%), rgba(255,255,255,0.78)`,
        }}
      />

      <div style={styles.content}>
        <div style={styles.iconStage}>
          <span style={{ ...styles.sheet, ...styles.sheetBack }} />
          <span style={{ ...styles.sheet, ...styles.sheetMid }} />
          <span style={{ ...styles.iconHalo, background: colorWithAlpha(primaryColor, 0.16) }} />
          <span
            style={{
              ...styles.iconGlass,
              boxShadow: `0 24px 52px ${colorWithAlpha(primaryColor, 0.2)}, inset 0 1px 0 rgba(255,255,255,0.72)`,
            }}
          >
            {!iconImageReady ? (
              <span style={{ ...styles.iconFallbackMark, color: primaryColor }}>{fallbackLetter}</span>
            ) : null}
            {loadedIconImage ? (
              <img
                src={loadedIconImage}
                alt={`${modeLabel} icon`}
                style={{ ...styles.iconImage, opacity: iconImageReady ? 1 : 0 }}
                onLoad={() => {
                  loadedLoadingIcons.add(loadedIconImage);
                  setIconImageReady(true);
                }}
              />
            ) : null}
          </span>
        </div>

        <div style={{ ...styles.badge, color: primaryColor, borderColor: colorWithAlpha(primaryColor, 0.25) }}>
          {modeLabel}
        </div>

        <h2 style={{ ...styles.title, color: primaryColor }}>{title}</h2>
        <p style={styles.detail}>{detail}</p>

        <div style={styles.loaderRow} aria-label={title} role="status">
          {[0, 1, 2].map((index) => (
            <span
              key={index}
              style={{
                ...styles.ball,
                background: primaryColor,
                animationDelay: `${index * 0.18}s`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    alignItems: "center",
    boxSizing: "border-box",
    display: "grid",
    inset: 0,
    justifyItems: "center",
    minHeight: "100dvh",
    padding: "24px",
    placeItems: "center",
    position: "fixed",
    transform: "none",
    width: "100vw",
    zIndex: 1200,
  },

  backdrop: {
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(circle at 50% 34%, rgba(95,125,77,0.18), transparent 34%), rgba(255,255,255,0.78)",
    backdropFilter: "blur(10px)",
  },

  backgroundImage: {
    height: "100%",
    inset: 0,
    objectFit: "cover",
    pointerEvents: "none",
    position: "absolute",
    width: "100%",
  },

  content: {
    position: "relative",
    width: "100%",
    maxWidth: "420px",
    padding: "20px 24px",
    textAlign: "center",
    transform: "none",
  },

  iconStage: {
    height: 156,
    margin: "0 auto 18px",
    position: "relative",
    width: 156,
  },

  sheet: {
    background: "rgba(255,255,255,0.62)",
    border: "1px solid rgba(190,202,232,0.7)",
    borderRadius: 8,
    boxShadow: "0 18px 42px rgba(23,32,51,0.12)",
    height: 92,
    left: 33,
    position: "absolute",
    top: 32,
    width: 90,
  },

  sheetBack: {
    animation: "pages-loading-sheet-back 2.8s ease-in-out infinite",
    transform: "rotate(-10deg) translate(-16px, 12px)",
  },

  sheetMid: {
    animation: "pages-loading-sheet-mid 2.8s ease-in-out infinite",
    transform: "rotate(8deg) translate(16px, 8px)",
  },

  iconHalo: {
    animation: "pages-loading-pulse 1.8s ease-in-out infinite",
    background: "rgba(95,125,77,0.16)",
    borderRadius: "50%",
    inset: 16,
    position: "absolute",
  },

  iconGlass: {
    alignItems: "center",
    animation: "pages-loading-float 2.2s ease-in-out infinite",
    backdropFilter: "blur(18px) saturate(1.2)",
    WebkitBackdropFilter: "blur(18px) saturate(1.2)",
    background: "rgba(255,255,255,0.82)",
    border: "1px solid rgba(255,255,255,0.82)",
    borderRadius: 8,
    boxShadow: "0 24px 52px rgba(95,125,77,0.2), inset 0 1px 0 rgba(255,255,255,0.72)",
    display: "flex",
    height: 86,
    justifyContent: "center",
    left: 35,
    position: "absolute",
    top: 35,
    width: 86,
  },

  iconImage: {
    height: 68,
    objectFit: "contain",
    position: "relative",
    transition: "opacity 120ms ease",
    width: 68,
    zIndex: 2,
  },

  iconFallbackMark: {
    alignItems: "center",
    background: "rgba(255,255,255,0.74)",
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: 8,
    display: "inline-flex",
    fontSize: 34,
    fontWeight: 900,
    height: 68,
    justifyContent: "center",
    left: 8,
    lineHeight: 1,
    position: "absolute",
    top: 8,
    width: 68,
    zIndex: 1,
  },

  badge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px 12px",
    borderRadius: "999px",
    border: "1px solid",
    background: "#fff",
    fontSize: "12px",
    fontWeight: "700",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    marginBottom: "14px",
  },

  title: {
    margin: "0 0 10px",
    fontSize: "24px",
    fontWeight: "700",
  },

  detail: {
    margin: "0 0 22px",
    color: "#475569",
    fontSize: "14px",
    lineHeight: "1.5",
  },

  loaderRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
  },

  ball: {
    width: "14px",
    height: "14px",
    borderRadius: "999px",
    animationName: "oikos-loading-bounce",
    animationDuration: "0.9s",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
  },
};
